// =====================================================================
// Impressions.jsx - Pagina principala unificata Impressions (Day 4)
// =====================================================================
// Layout Option C: 1 link in sidebar (/impressions), 4 vue-uri interne.
//
// Tabs:
//   1. Impressions       - ImpressionsList embedded (jobs list + filtre + form)
//   2. Facturation       - pivot Client x mois + bulk invoice/uninvoice
//   3. Clients           - ClientsTab din PrintingCatalogsAdmin (admin catalog)
//   4. Tâches            - TemplatesTab din PrintingCatalogsAdmin (admin catalog)
//
// Permission gates:
//   - tabs 1: printing_jobs.read.{all,own}
//   - tab 2: printing_jobs.invoice.all
//   - tabs 3-4: printing_jobs.manage_catalog.all
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { printingJobs as jobsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import ImpressionsList from './ImpressionsList.jsx';
import { ClientsTab, TemplatesTab } from '../admin/PrintingCatalogsAdmin.jsx';
import Breadcrumbs from '../../components/Breadcrumbs.jsx';

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

const inputBase = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.8125rem',
  background: '#fff',
};

const btnSuccess = {
  background: '#059669', color: '#fff', border: 0,
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSecondary = {
  background: 'white', color: '#1e293b', border: '1px solid #d1d5db',
  padding: '0.5rem 1rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const thStyle = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '0.625rem 0.75rem',
  verticalAlign: 'middle',
  color: '#374151',
  fontSize: '0.875rem',
};

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

// =====================================================================
// MAIN
// =====================================================================
export default function Impressions() {
  const { can } = useAuth();

  const canRead          = can(['printing_jobs.read.all', 'printing_jobs.read.own']);
  const canInvoice       = can('printing_jobs.invoice.all');
  const canManageCatalog = can('printing_jobs.manage_catalog.all');

  // Default tab: cel mai puternic permission decide focus-ul
  const defaultTab = canRead ? 'impressions' : (canInvoice ? 'facturation' : (canManageCatalog ? 'clients' : 'impressions'));
  const [activeTab, setActiveTab] = useState(defaultTab);

  const TAB_LABELS = {
    'impressions': 'Impressions',
    'facturation': 'Facturation',
    'clients':     'Clients',
    'templates':   'Tâches',
  };

  if (!canRead && !canInvoice && !canManageCatalog) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Printing rapport</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Printing rapport</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-003 — jobs &amp; re-facturation</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Jobs d'impression et re-facturation aux clients.
      </p>

      <Breadcrumbs items={[
        { label: 'Printing rapport', onClick: () => setActiveTab(defaultTab) },
        { label: TAB_LABELS[activeTab] || activeTab },
      ]} />

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canRead && (
          <button style={tabBtn(activeTab === 'impressions')} onClick={() => setActiveTab('impressions')}>
            🖨️ Impressions
          </button>
        )}
        {canInvoice && (
          <button style={tabBtn(activeTab === 'facturation')} onClick={() => setActiveTab('facturation')}>
            💰 Facturation
          </button>
        )}
        {canManageCatalog && (
          <button style={tabBtn(activeTab === 'clients')} onClick={() => setActiveTab('clients')}>
            👥 Clients
          </button>
        )}
        {canManageCatalog && (
          <button style={tabBtn(activeTab === 'templates')} onClick={() => setActiveTab('templates')}>
            📋 Tâches
          </button>
        )}
      </div>

      {activeTab === 'impressions' && canRead          && <ImpressionsList embedded />}
      {activeTab === 'facturation' && canInvoice       && <FacturationTab />}
      {activeTab === 'clients'     && canManageCatalog && <ClientsTab />}
      {activeTab === 'templates'   && canManageCatalog && <TemplatesTab />}
    </div>
  );
}

// =====================================================================
// TAB FACTURATION (pivot Client x Mois)
// =====================================================================
function FacturationTab() {
  const [jobs, setJobs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy]       = useState(false);

  const [year, setYear]   = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo  = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const res = await jobsApi.list({ dateFrom, dateTo, pageSize: 500 });
      setJobs(res.items || []);
    } catch (err) {
      console.error('[Impressions/FacturationTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Build pivot: byClient
  const pivot = (() => {
    const byClient = new Map();
    for (const j of jobs) {
      const ckey = j.clientId || '(none)';
      const cname = j.client?.label || j.client?.code || '(sans client)';
      const ccode = j.client?.code || '';

      if (!byClient.has(ckey)) {
        byClient.set(ckey, {
          key: ckey, name: cname, code: ccode,
          jobs: [],
          allInvoiced: true,
          totalPages: 0, totalBW: 0, totalColor: 0,
        });
      }
      const cl = byClient.get(ckey);
      cl.jobs.push(j);
      cl.totalPages += j.pages || 0;
      if (j.couleur === 'BW')    cl.totalBW    += j.pages || 0;
      if (j.couleur === 'COLOR') cl.totalColor += j.pages || 0;
      if (!j.invoiced) cl.allInvoiced = false;
    }
    for (const cl of byClient.values()) {
      cl.jobs.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }
    return Array.from(byClient.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  function toggleClient(cl) {
    const next = new Set(selectedIds);
    const eligible = cl.jobs.filter(j => !j.invoiced).map(j => j.id);
    const allSelected = eligible.length > 0 && eligible.every(id => next.has(id));
    if (allSelected) eligible.forEach(id => next.delete(id));
    else eligible.forEach(id => next.add(id));
    setSelectedIds(next);
  }

  function toggleJob(jobId, invoiced) {
    if (invoiced) return;
    const next = new Set(selectedIds);
    if (next.has(jobId)) next.delete(jobId);
    else next.add(jobId);
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    const eligible = jobs.filter(j => !j.invoiced).map(j => j.id);
    if (eligible.every(id => selectedIds.has(id))) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligible));
  }

  async function handleBulkInvoice() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Marquer ${selectedIds.size} job(s) comme facturé(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await jobsApi.bulkInvoice({ ids: Array.from(selectedIds) });
      alert(`Facturés: ${res.invoiced || 0}\nDéjà facturés (skip): ${res.alreadyInvoiced || 0}\nIntrouvables: ${res.notFound || 0}`);
      await fetchJobs();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkUninvoice() {
    if (selectedIds.size === 0) return;
    if (!confirm(`Annuler la facturation pour ${selectedIds.size} job(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await jobsApi.bulkUninvoice({ ids: Array.from(selectedIds) });
      alert(`Réversés: ${res.uninvoiced || 0}\nDéjà non facturés (skip): ${res.alreadyUninvoiced || 0}\nIntrouvables: ${res.notFound || 0}`);
      await fetchJobs();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  const years = [year - 1, year, year + 1];
  const months = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' }, { v: 4, l: 'Avril' },
    { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' }, { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' },
    { v: 9, l: 'Septembre' }, { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];

  const grand = pivot.reduce((acc, c) => ({
    pages: acc.pages + c.totalPages,
    bw:    acc.bw    + c.totalBW,
    color: acc.color + c.totalColor,
  }), { pages: 0, bw: 0, color: 0 });

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Période:</span>
        <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={inputBase}>
          {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} style={inputBase}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
          <button style={{ ...btnSuccess, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkInvoice}>
            ✓ Marquer facturé
          </button>
          <button style={{ ...btnSecondary, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkUninvoice}>
            Annuler facturation
          </button>
        </div>
      </div>

      {!loading && jobs.length > 0 && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.625rem 0.875rem', background: '#f9fafb', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem', color: '#374151' }}>
          <span>📊 <strong>{jobs.length}</strong> jobs</span>
          <span>Σ pages: <strong>{grand.pages.toLocaleString('fr-BE')}</strong></span>
          <span>N&amp;B: <strong>{grand.bw.toLocaleString('fr-BE')}</strong></span>
          <span style={{ color: '#0369a1' }}>Couleur: <strong>{grand.color.toLocaleString('fr-BE')}</strong></span>
        </div>
      )}

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && jobs.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 6 }}>
          Aucun job pour cette période.
        </div>
      )}

      {!loading && jobs.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={jobs.filter(j => !j.invoiced).every(j => selectedIds.has(j.id)) && jobs.some(j => !j.invoiced)}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={thStyle}>Client / Job</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Tâche</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pages</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Couleur</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {pivot.map(cl => (
                <ClientRows key={cl.key} cl={cl} selectedIds={selectedIds} onToggleClient={() => toggleClient(cl)} onToggleJob={toggleJob} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ClientRows({ cl, selectedIds, onToggleClient, onToggleJob }) {
  const eligibleIds = cl.jobs.filter(j => !j.invoiced).map(j => j.id);
  const totalSel = eligibleIds.filter(id => selectedIds.has(id)).length;
  const allSel  = eligibleIds.length > 0 && totalSel === eligibleIds.length;
  const someSel = totalSel > 0 && totalSel < eligibleIds.length;

  return (
    <>
      <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem' }}>
          <input
            type="checkbox"
            checked={allSel}
            ref={el => { if (el) el.indeterminate = someSel; }}
            onChange={onToggleClient}
            disabled={eligibleIds.length === 0}
          />
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#111827' }} colSpan={3}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#475569', marginRight: 6 }}>{cl.code}</span>
          {cl.name}
          <span style={{ marginLeft: 8, fontSize: '0.6875rem', color: '#6b7280', fontWeight: 400 }}>
            · {cl.jobs.length} job{cl.jobs.length > 1 ? 's' : ''}
          </span>
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{cl.totalPages.toLocaleString('fr-BE')}</td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.6875rem', color: '#6b7280' }}>
          {cl.totalBW > 0 && <span>N&amp;B {cl.totalBW}</span>}
          {cl.totalBW > 0 && cl.totalColor > 0 && <span> · </span>}
          {cl.totalColor > 0 && <span style={{ color: '#0369a1' }}>Col {cl.totalColor}</span>}
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', textAlign: 'center' }}>
          <span style={{
            background: cl.allInvoiced ? '#d1fae5' : '#fef3c7',
            color: cl.allInvoiced ? '#065f46' : '#92400e',
            padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500,
          }}>
            {cl.allInvoiced ? '✓ Tous facturés' : 'Non facturé'}
          </span>
        </td>
      </tr>
      {cl.jobs.map(j => (
        <tr key={j.id} style={{ borderTop: '1px solid #f3f4f6' }}>
          <td style={tdStyle}>
            <input
              type="checkbox"
              checked={selectedIds.has(j.id)}
              onChange={() => onToggleJob(j.id, j.invoiced)}
              disabled={j.invoiced}
            />
          </td>
          <td style={{ ...tdStyle, padding: '0.4rem 0.75rem 0.4rem 2rem', fontSize: '0.75rem', fontFamily: 'monospace', color: '#6b7280' }}>{j.ref}</td>
          <td style={tdStyle}>{formatDateFR(j.date)}</td>
          <td style={tdStyle}>{j.taskTemplate?.label || '—'}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{(j.pages || 0).toLocaleString('fr-BE')}</td>
          <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.6875rem' }}>
            <span style={{
              background: j.couleur === 'COLOR' ? '#e0f2fe' : '#f1f5f9',
              color: j.couleur === 'COLOR' ? '#0369a1' : '#475569',
              padding: '0.125rem 0.5rem', borderRadius: 12, fontWeight: 500,
            }}>
              {j.couleur === 'COLOR' ? 'Couleur' : 'N&B'}
            </span>
          </td>
          <td style={{ ...tdStyle, textAlign: 'center' }}>
            <span style={{
              background: j.invoiced ? '#d1fae5' : '#fef3c7',
              color: j.invoiced ? '#065f46' : '#92400e',
              padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500,
            }}>
              {j.invoiced ? '✓' : '—'}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
}
